import {createSelector} from 'reselect';
import isempty from 'lodash/isEmpty';
import get from 'lodash/get';
import {getTranslations, getListingPage} from './store';
import {labelMaker} from '../../server/utils/labelMaker';
import {log as logToServer} from '../api/logger';
import {priceErrorMap} from '../utils/errorEvents';
import {getPriceDetails} from './priceDetails';
import {getSleeps} from './listing';

// This is the priceDetails from the graphql-traveler-checkout partials.
// This note is important until we remove all the PDS related details.
const getCheckoutError = createSelector(
    [getListingPage],
    (listingPage) => {
        const priceDetails =
            listingPage.priceDetails && listingPage.priceDetails.toJS
                ? listingPage.priceDetails.toJS()
                : listingPage.priceDetails || {};

        return priceDetails && priceDetails.originalErrorCode
            ? {
                  errorCode: priceDetails.originalErrorCode,
                  statusCode: get(priceDetails, 'errors[0].extensions.statusCode')
              }
            : null;
    }
);

const getErrorCode = createSelector(
    [getCheckoutError],
    (checkoutError) => {
        return checkoutError || null;
    }
);

const getErrorObj = createSelector(
    [getErrorCode],
    (error) => {
        let errorObj;

        if (error) {
            const {errorCode} = error;

            if (errorCode) {
                errorObj = priceErrorMap[errorCode] || priceErrorMap.UNDEFINED;

                if (!priceErrorMap[errorCode]) {
                    logToServer({
                        message: 'unhandled error',
                        errorCode
                    });
                }
            }
        }

        return errorObj || null;
    }
);

const getErrorMessage = createSelector(
    [getErrorObj, getCheckoutError, getTranslations],
    (errorObj, checkoutError, translations) => {
        return (
            (!!errorObj && translations[errorObj.translationKey]) ||
            (!!checkoutError && checkoutError.message) ||
            null
        );
    }
);

const getLabelWithData = createSelector(
    [getErrorObj, getSleeps, getTranslations],
    (errorObj, sleeps, translations) => {
        if (!!errorObj && errorObj.requiresDataForTranslation) {
            // for now, there's only one message we're handling that needs this, too many guests.
            // if/when more come along, we'll have to refactor this to handle the data the others need.
            return Object.assign({}, errorObj, {
                label: labelMaker(translations[errorObj.translationKey], sleeps)
            });
        }

        return null;
    }
);

const getPriceError = createSelector(
    [getErrorObj, getErrorCode, getLabelWithData, getErrorMessage],
    (errorObj, errorCode, labelWithData, errorMessage) => {
        if (labelWithData) {
            return labelWithData;
        }

        if (errorMessage) {
            return {
                ...errorObj,
                label: errorMessage,
                isUpstreamServiceError: errorCode && errorCode.statusCode >= 500
            };
        }

        return null;
    }
);

const getHasPriceError = createSelector(
    [getCheckoutError],
    (checkoutError) => !!checkoutError
);

const getAvailable = createSelector(
    [getPriceDetails, getCheckoutError],
    (priceDetails, checkoutError) => {
        return !isempty(priceDetails) && !checkoutError;
    }
);

const getPriceStatus = createSelector(
    [getAvailable, getHasPriceError, getPriceError],
    (isAvailable, hasPriceError, priceError) => {
        return {
            isAvailable,
            hasPriceError,
            priceError
        };
    }
);

export default getPriceStatus;
export {
    getAvailable,
    getErrorObj,
    getErrorCode,
    getErrorMessage,
    getLabelWithData,
    getCheckoutError,
    getPriceError,
    getPriceStatus
};
